import CryptoJS from 'crypto-js';
import axios, { AxiosRequestConfig, AxiosResponse, AxiosResponseHeaders } from 'axios';
import cacheData from 'memory-cache';
import { PHASE_PRODUCTION_BUILD } from 'next/constants';

type Code = 'unprocessable_entity' | 'bad_request';

export type ErrorCode =
  | 'invalid'
  | 'invalid_characters'
  | 'confirmation'
  | 'invalid_phone_number'
  | 'wrong_length'
  | 'invalid_post_code'
  | 'price_changed'
  | 'unavailable_plan'
  | 'too_long'
  | 'invalid_discount_id'
  | 'invalid_zenkoku_id'
  | 'blacklisted_email'
  | 'invalid_idtoken';

export interface ValidationError extends Error {
  response?: {
    data?: {
      code?: Code;
      detail?: {
        key:
          | 'firstName'
          | 'lastName'
          | 'firstNameKana'
          | 'lastNameKana'
          | 'email'
          | 'emailConfirmation'
          | 'phoneNumber'
          | 'prefecture'
          | 'countryCode'
          | 'postalCode'
          | 'address';
        errorCode: ErrorCode;
        message?: string;
      }[];
    };
  };
}

type HttpMethod = Extract<AxiosRequestConfig['method'], 'GET' | 'POST' | 'DELETE' | 'HEAD'>;

type RequestParams = {
  url: string;
  method: Extract<
    AxiosRequestConfig['method'],
    Extract<HttpMethod, 'GET'> | Extract<HttpMethod, 'DELETE'> | Extract<HttpMethod, 'HEAD'>
  >;
  headers?: AxiosRequestConfig['headers'];
  data?: never;
  signal?: AbortSignal;
  validate?: () => boolean;
};

type RequestParamsWithData<D> = {
  url: string;
  method: Extract<
    AxiosRequestConfig['method'],
    Extract<HttpMethod, 'POST'> | Extract<HttpMethod, 'DELETE'>
  >;
  headers?: AxiosRequestConfig['headers'];
  data?: D;
  signal?: AbortSignal;
  validate?: () => boolean;
};

/**
 * `timeout` specifies the number of milliseconds before the request times out.
 * If the request takes longer than `timeout`, the request will be aborted.
 */
const timeout = 40 * 1000;
const timeoutErrorMessage = 'REQUEST_TIMEOUT';

/**
 * `validateStatus` defines whether to resolve or reject the promise for a given
 * HTTP response status code. If `validateStatus` returns `true` (or is set to `null`
 *  or `undefined`), the promise will be resolved; otherwise, the promise will be
 *  rejected.
 *  @param status HTTP response status code
 */
function validateStatus(status: number) {
  return status >= 200 && status < 300; // default
}

async function request<T, D = unknown>({
  url,
  method,
  headers,
  data,
  validate,
  signal,
}: RequestParams | RequestParamsWithData<D>): Promise<T> {
  const serverHeaders: Record<string, string> = {};

  // Add custom user agent if the request is made from the server so that BFF can whitelist this request
  if (typeof window === 'undefined') {
    serverHeaders['User-Agent'] = `Direct-Channel-Web-Vercel/${process.env.PACKAGE_VERSION}`;
  }

  const axiosResponse = await axios.request<T, AxiosResponse<T> | AxiosResponseHeaders, D>({
    url,
    method,
    data,
    validateStatus: validate || validateStatus,
    timeout,
    timeoutErrorMessage,
    headers: {
      ...headers,
      ...serverHeaders,
    },
    signal,
  });

  if (method === 'HEAD') return axiosResponse.headers as unknown as T;
  return axiosResponse.data as T;
}

const DEFAULT_SERVERLESS_API_CALL_CACHE_TIME = process.env.SERVERLESS_API_CALL_CACHE_TIME
  ? Number(process.env.SERVERLESS_API_CALL_CACHE_TIME)
  : 0;

// the request function is for caching the client-fetched data.
// it helps us to reduce the number of API calls when building the app.
// @see https://solutions-reuse-responses.vercel.app/
// @see https://stackoverflow.com/questions/62005208/api-caching-for-next-js
// default cache time is 900 seconds (15 minutes)
export async function requestWithCache<T, D = unknown>({
  cacheTime = DEFAULT_SERVERLESS_API_CALL_CACHE_TIME,
  ...rest
}: (RequestParams | RequestParamsWithData<D>) & {
  cacheTime?: number;
}): Promise<T> {
  const { url, data, method } = rest;
  const urlWithData = `${method}:${url}?${data ? JSON.stringify(data) : ''}`;
  const hash = CryptoJS.SHA256(urlWithData).toString();
  const value = cacheData.get(hash);
  if (value) {
    return value;
  }

  const res = request<T, D>({
    ...rest,
  });

  if (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD && cacheTime > 0) {
    cacheData.put(hash, res, cacheTime * 1000);
  }

  return res;
}

export default request;
